import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import { API, errorText } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { setStudentToken } from "@/lib/learnApi";

// Opened from the emailed sign-in link: /login#link=<token>. The token is in the hash so it never reaches server logs.
export const LearnAuthCallback = ({ hash }) => {
  const nav = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = new URLSearchParams(hash.replace(/^#/, "")).get("link");
    window.history.replaceState(null, "", "/login");
    axios.post(`${API}/learn/link/verify`, { token })
      .then(({ data }) => { setStudentToken(data.token); nav("/login", { replace: true }); })
      .catch((e) => setError(errorText(e)));
  }, [hash, nav]);
  return (
    <div className="grid min-h-screen place-items-center bg-[#0B1B33] px-5 text-center text-white">
      {error ? (
        <div data-testid="learn-auth-error" className="max-w-sm space-y-4 rounded-2xl bg-white p-8 text-[#0B1B33]">
          <p className="font-display text-[20px] font-bold">Sign-in link not valid</p><p className="text-[15px] text-slate-700">{error}</p>
          <button onClick={() => { window.location.href = "/login"; }} className="btn-teal w-full rounded-lg py-3 font-semibold">Back to sign in</button>
        </div>
      ) : <Loader2 className="h-8 w-8 animate-spin" />}
    </div>
  );
};

const EmailLink = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API}/learn/link`, { email }); setSent(true); }
    catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  if (sent) return (
    <div data-testid="learn-link-sent" className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-[14px] text-teal-900">
      <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Check your inbox</p>
      <p className="mt-1">If <b>{email}</b> is enrolled, a sign-in link is on its way. It works once and expires in 20 minutes.</p>
      <button onClick={() => setSent(false)} className="mt-2 font-semibold text-[#0E9384] underline">Use a different email</button>
    </div>
  );
  return (
    <form data-testid="learn-link-form" onSubmit={submit} className="space-y-3">
      <input data-testid="learn-link-email" type="email" required placeholder="Email you enrolled with" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 w-full rounded-lg border border-slate-300 px-3 outline-none focus:border-[#0E9384]" />
      <button data-testid="learn-link-btn" disabled={busy} className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-3.5 font-semibold disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Mail className="h-5 w-5" /> Email me a sign-in link</>}
      </button>
    </form>
  );
};

export const LearnLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [usePw, setUsePw] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/learn/login`, { email, password });
      setStudentToken(data.token);
      onLogin();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="grain relative hidden flex-col justify-between overflow-hidden bg-[#0B1B33] p-12 lg:flex">
        <Logo light sub="LEARNING PORTAL" testId="learn-logo-side" />
        <div>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight text-white">Everything for your cohort, in one place.</h1>
          <p className="mt-4 max-w-md text-lg text-slate-300">Join live Zoom sessions in one click, rewatch recordings, grab slides and checklists, and submit your projects.</p>
        </div>
        <p className="text-[13px] text-slate-400">Only enrolled ArtOfResume learners can sign in.</p>
      </div>
      <div className="flex items-center justify-center bg-[#F7FAFC] px-5 py-12">
        <div data-testid="learn-login-card" className="w-full max-w-sm space-y-5">
          <div className="lg:hidden"><Logo sub="LEARNING PORTAL" testId="learn-logo" /></div>
          <div><h2 className="font-display text-[28px] font-bold text-[#0B1B33]">Sign in to learn</h2><p className="text-[15px] text-slate-600">Use the email you enrolled with.</p></div>
          {!usePw ? <>
            <EmailLink />
            <button data-testid="learn-use-password" onClick={() => setUsePw(true)} className="w-full text-center text-[13px] font-semibold text-slate-600 hover:text-[#0B1B33]">I have a password</button>
          </> : <>
          <form onSubmit={submit} className="space-y-3">
            <input data-testid="learn-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 w-full rounded-lg border border-slate-300 px-3 outline-none focus:border-[#0E9384]" />
            <input data-testid="learn-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 w-full rounded-lg border border-slate-300 px-3 outline-none focus:border-[#0E9384]" />
            <button data-testid="learn-login-btn" disabled={busy} className="btn-teal w-full rounded-xl py-3.5 font-semibold disabled:opacity-60">{busy ? "Signing in..." : "Sign in"}</button>
          </form>
          <button onClick={() => setUsePw(false)} className="w-full text-center text-[13px] font-semibold text-slate-600 hover:text-[#0B1B33]">Email me a sign-in link instead</button>
          </>}
          <p className="text-[13px] text-slate-600">Not enrolled yet? <a href="/go#pathway" className="font-semibold text-[#0E9384]">Explore the pathway</a></p>
        </div>
      </div>
    </div>
  );
};
